import API_URL from "../config/ApiUrl";
const API_BASE_URL = `${API_URL}/api/grow-room`;

class GrowRoomAPI {
  
  // Check if user has a grow room
  static async getGrowRoomStatus() {
    try {
      const response = await fetch(`${API_BASE_URL}/status`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking grow room status:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Create a new grow room
  static async createGrowRoom() {
    try {
      const response = await fetch(`${API_BASE_URL}/create`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating grow room:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Get complete grow room data for the game
  static async getGrowRoomData() {
    try {
      const response = await fetch(`${API_BASE_URL}/data`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching grow room data:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Check offline degradation
  static async checkOfflineDegradation() {
    try {
      const response = await fetch(`${API_BASE_URL}/check-offline`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking offline degradation:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Plant a seed in a pot
  static async plantSeed(potIndex, seedType) {
    try {
      const response = await fetch(`${API_BASE_URL}/plant-seed`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pot_index: potIndex,
          seed_type: seedType
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error planting seed:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Update plant after tool use
  static async updatePlant(potIndex, updates) {
    try {
      const response = await fetch(`${API_BASE_URL}/update-plant`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pot_index: potIndex,
          updates: updates
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating plant:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Harvest a plant
  static async harvestPlant(potIndex, points, seedsEarned) {
    try {
      const response = await fetch(`${API_BASE_URL}/harvest-plant`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pot_index: potIndex,
          points: points,
          seeds_earned: seedsEarned
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error harvesting plant:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Remove a plant
  static async removePlant(potIndex, points) {
    try {
      const response = await fetch(`${API_BASE_URL}/remove-plant`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pot_index: potIndex,
          points: points
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error removing plant:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Claim daily bonus
  static async claimDailyBonus() {
    try {
      const response = await fetch(`${API_BASE_URL}/daily-bonus`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error claiming daily bonus:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Sync game state to backend (for periodic saves)
  static async syncGameState(gameState) {
    try {
      const response = await fetch(`${API_BASE_URL}/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          points: gameState.points,
          streak: gameState.streak,
          garden_health: gameState.gardenHealth,
          total_harvests: gameState.totalHarvests,
          plants: gameState.plants.map(plant => ({
            id: plant.id,
            plant_type: plant.plantType,
            stage: plant.stage,
            health: plant.health,
            growth_progress: plant.growth,
            water_level: plant.waterLevel,
            light_level: plant.lightLevel,
            soil_quality: plant.soilQuality,
            last_watered: plant.lastWatered,
            needs_attention: plant.needsAttention,
            is_harvested: plant.isRemoved
          })),
          inventory: gameState.inventory
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error syncing game state:', error);
      return { success: false, error: error.message };
    }
  }

   static async updatePotPosition(potIndex, newX, newZ) {
    try {
      const response = await fetch(`${API_BASE_URL}/update-pot-position`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pot_index: potIndex,
          position_x: newX,
          position_z: newZ
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating pot position:', error);
      return { success: false, error: error.message };
    }
  }

  // Get all pot positions (useful for validation)
  static async getPotPositions() {
    try {
      const response = await fetch(`${API_BASE_URL}/pot-positions`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching pot positions:', error);
      return { success: false, error: error.message };
    }
  }
    static async getShopCatalog() {
    try {
      const response = await fetch(`${API_BASE_URL}/shop/catalog`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching shop catalog:', error);
      return { success: false, error: error.message };
    }
  }

  // Purchase individual seeds
  static async purchaseSeed(seedType, quantity = 1) {
    try {
      const response = await fetch(`${API_BASE_URL}/shop/purchase-seed`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          seed_type: seedType,
          quantity: quantity
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error purchasing seed:', error);
      return { success: false, error: error.message };
    }
  }

  // Purchase seed bundles
  static async purchaseBundle(bundleName) {
    try {
      const response = await fetch(`${API_BASE_URL}/shop/purchase-bundle`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bundle_name: bundleName
        }),
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error purchasing bundle:', error);
      return { success: false, error: error.message };
    }
  }

  // Get purchase history
  static async getPurchaseHistory(page = 1, perPage = 20) {
    try {
      const response = await fetch(`${API_BASE_URL}/shop/purchase-history?page=${page}&per_page=${perPage}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching purchase history:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user's current inventory and points (for shop display)
  static async getShopUserData() {
    try {
      const response = await fetch(`${API_BASE_URL}/shop/user-data`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching shop user data:', error);
      return { success: false, error: error.message };
    }
  }
   static async updateAllPlants() {
    try {
      const response = await fetch(`${API_BASE_URL}/update-all-plants`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating all plants:', error);
      return { success: false, error: error.message };
    }
  }

}

export default GrowRoomAPI;